import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { GridConfig } from '../../types/image-editor.types';

interface GridSettingsDialogProps {
  open: boolean;
  config: GridConfig;
  onApply: (next: GridConfig) => void;
  onClose: () => void;
}

/** Modal for configuring the measurement grid — Photoshop's
 *  View → Show → Grid Settings. Three knobs: scene-space spacing
 *  between major lines, number of minor subdivisions per cell,
 *  and stroke color. Apply commits via `onApply`; Cancel /
 *  outside-click discards via `onClose`.
 *
 *  Local state mirrors the prop on open so editing the inputs
 *  doesn't dispatch on every keystroke. The parent only sees the
 *  final values on Apply. */
export function GridSettingsDialog({ open, config, onApply, onClose }: GridSettingsDialogProps) {
  const [draft, setDraft] = useState<GridConfig>(config);
  useEffect(() => {
    if (open) setDraft(config);
  }, [open, config]);

  const handleSize = (raw: string) => {
    const n = Number.parseFloat(raw);
    if (Number.isFinite(n) && n > 0) setDraft((d) => ({ ...d, size: n }));
  };
  const handleSubdivisions = (raw: string) => {
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1) setDraft((d) => ({ ...d, subdivisions: n }));
  };
  const handleColor = (raw: string) => setDraft((d) => ({ ...d, color: raw }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Grid Settings</DialogTitle>
          <DialogDescription className="sr-only">
            Configure the measurement grid overlay: spacing between major lines, number of
            subdivisions per cell, and stroke color.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-3 items-center gap-2">
            <Label htmlFor="grid-size">Gridline every</Label>
            <Input
              id="grid-size"
              type="number"
              min={1}
              step={1}
              value={draft.size}
              onChange={(e) => handleSize(e.target.value)}
              className="col-span-2"
            />
          </div>
          <div className="grid grid-cols-3 items-center gap-2">
            <Label htmlFor="grid-subdivisions">Subdivisions</Label>
            <Input
              id="grid-subdivisions"
              type="number"
              min={1}
              step={1}
              value={draft.subdivisions}
              onChange={(e) => handleSubdivisions(e.target.value)}
              className="col-span-2"
            />
          </div>
          <div className="grid grid-cols-3 items-center gap-2">
            <Label htmlFor="grid-color">Color</Label>
            <Input
              id="grid-color"
              type="color"
              value={draft.color}
              onChange={(e) => handleColor(e.target.value)}
              className="col-span-2 h-9 p-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
